import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getEntriesByContentTypeAndProduct } from 'api/contentful';

import CopyButton from 'components/CopyButton';
import { H1, H4 } from 'components/Typography';

import Accordion from 'components/Accordion';
import {
  policiesNomadCitizen,
  policiesNIC,
  policiesNH,
  policiesCommunityRH,
  policiesCommunityStarterRH,
  policiesCommunityPersonalRH,
  policiesCommunityLifeRH,
  policiesIndividualsRH,
  policiesRHUSA,
  archiveNI,
  archiveNIC,
  archiveNH,
  archiveCommunityRH,
  archiveAffinityRH,
  archiveIndividualsRH
} from './content';

import { PoliciesContainer, Policy, PolicyName, Product } from './styled';

const PolicyList = ({ policies }) =>
  policies.map((policy, i) => {
    const policyLink = policy.link.startsWith('https://')
      ? policy.link
      : `${window.location.origin}${policy.link}`;
    return (
      <Policy key={i}>
        <CopyButton value={policyLink} />
        <PolicyName href={policyLink}>{policy.name}</PolicyName>
      </Policy>
    );
  });

PolicyList.propTypes = {
  policies: PropTypes.array.isRequired
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: '2-digit',
    year: 'numeric'
  });
};

const Policies = () => {
  const [carrierPolicies, setCarrierPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCarrierPolicies = async () => {
    const data = await getEntriesByContentTypeAndProduct('policy', 'nomad-insurance', 5);
    const sortedData = data.sort(
      (a, b) => new Date(b.fields.effectiveDate) - new Date(a.fields.effectiveDate)
    );
    setCarrierPolicies(sortedData);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCarrierPolicies();
  }, []);

  const allCarrierPolicies =
    carrierPolicies.length > 0
      ? carrierPolicies.flatMap((policy) => {
          const effectiveDate = formatDate(policy.fields.effectiveDate);

          if (policy.fields.alternativeDocumentPdf) {
            // if policy has policyAlternativePdf, it means that it has two PDFs
            // the older one is the policyAlternativePdf, newer is the policyDocumentPdf
            // we need to display both
            const olderPdfEffectiveUntil = formatDate(
              policy.fields.alternativeDocumentPdf.fields.effectiveUntil
            );
            return [
              {
                name: `${policy.fields.nameOnPoliciesPage} | Carrier - Dynamic (${effectiveDate})`,
                link: `/nomad-insurance/policy?version=${encodeURIComponent(
                  policy.fields.effectiveDate
                )}`
              },
              {
                name: `${policy.fields.nameOnPoliciesPage} | Carrier - PDF (${olderPdfEffectiveUntil})`,
                link: `https://${policy.fields.policyDocumentPdf.fields.file.url}`
              },
              {
                name: `${policy.fields.nameOnPoliciesPage} | Carrier - PDF (${effectiveDate})`,
                link: `https://${policy.fields.alternativeDocumentPdf.fields.pdfFile.fields.file.url}`
              }
            ];
          }
          return [
            {
              name: `${policy.fields.nameOnPoliciesPage} | Carrier - Dynamic (${effectiveDate})`,
              link: `/nomad-insurance/policy?version=${encodeURIComponent(
                policy.fields.effectiveDate
              )}`
            },
            {
              name: `${policy.fields.nameOnPoliciesPage} | Carrier - PDF (${effectiveDate})`,
              link: `https://${policy.fields.policyDocumentPdf.fields.file.url}`
            }
          ];
        })
      : [];

  const [
    firstCarrierPolicy = { name: '', link: '' },
    firstCarrierPolicyPdf = { name: '', link: '' },
    ...remainingCarrierPolicies
  ] = allCarrierPolicies;

  const updatedArchiveNI = [...remainingCarrierPolicies, ...archiveNI];

  return (
    <PoliciesContainer bg="light" isLoading={isLoading}>
      <H1>All policy versions</H1>
      <Product>
        <H4>Nomad Citizen policy versions</H4>
        <PolicyList policies={policiesNomadCitizen} />
      </Product>

      <Product>
        <H4>Nomad Insurance Essential policy versions</H4>
        <PolicyList policies={[firstCarrierPolicy, firstCarrierPolicyPdf]} />
      </Product>

      <Product>
        <H4>Nomad Insurance Complete policy versions</H4>
        <PolicyList policies={policiesNIC} />
      </Product>

      <Product>
        <H4>Remote Health Community policy versions</H4>
        <PolicyList policies={policiesCommunityRH} />
      </Product>

      <Product>
        <H4>Remote Health Community Starter policy versions</H4>
        <PolicyList policies={policiesCommunityStarterRH} />
      </Product>

      <Product>
        <H4>Remote Health Community Personal policy versions</H4>
        <PolicyList policies={policiesCommunityPersonalRH} />
      </Product>

      <Product>
        <H4>Remote Health Community policy + Life versions</H4>
        <PolicyList policies={policiesCommunityLifeRH} />
      </Product>

      <Product>
        <H4>Remote Health individuals versions</H4>
        <PolicyList policies={policiesIndividualsRH} />
      </Product>

      <Product>
        <H4>Remote Health USA</H4>
        <PolicyList policies={policiesRHUSA} />
      </Product>

      <Product>
        <H4>Nomad Health versions</H4>
        <PolicyList policies={policiesNH} />
      </Product>

      <Product>
        <H4>Archived policies</H4>
        <Accordion title="Nomad Insurance" isExpanded={false}>
          <PolicyList policies={updatedArchiveNI} />
        </Accordion>
        <Accordion title="Nomad Insurance Complete" isExpanded={false}>
          <PolicyList policies={archiveNIC} />
        </Accordion>
        <Accordion title="Nomad Health" isExpanded={false}>
          <PolicyList policies={archiveNH} />
        </Accordion>
        <Accordion title="Remote Health Community" isExpanded={false}>
          <PolicyList policies={archiveCommunityRH} />
        </Accordion>
        <Accordion title="Remote Health Individuals" isExpanded={false}>
          <PolicyList policies={archiveIndividualsRH} />
        </Accordion>
        <Accordion title="Remote Health Affinity" isExpanded={false}>
          <PolicyList policies={archiveAffinityRH} />
        </Accordion>
      </Product>
    </PoliciesContainer>
  );
};

export default Policies;
